"use client";

import { createContext, useContext } from "react";
import type { GA4Tracking } from "./ga4";

/**
 * 末端コンポーネントに公開するトラッキング契約（最小表面）。
 * 実装詳細（setUserId/updateConsent等）はContext内部に閉じ、
 * 画面側は trackEvent だけを触る。
 *
 * 将来GA4を独自サーバーに差し替える場合も、この契約を満たす実装を用意し、
 * 対応するProvider/Trackerコンポーネントをlayoutで差し替える。
 *
 * @knipignore
 */
export type Tracker = {
  trackEvent: (
    eventName: string,
    params?: Record<string, unknown>,
  ) => Promise<void>;
};

// Context値: tracking インスタンスと measurementId を束ねて保持する。
// null = SSR中 / 計測無効。
// - 末端コンポーネントは useTracking() 経由で Tracker 契約のみを参照する
// - GA4Tracker は useContext(TrackingContext) で値全体を参照し、GA4への副作用を発火する
export type TrackingContextValue = {
  tracking: GA4Tracking;
  measurementId: string;
} | null;

export const TrackingContext = createContext<TrackingContextValue>(null);

// 初期化前・計測無効時のフォールバック: 何もせず resolve するだけの no-op
const noop: Tracker["trackEvent"] = async () => {};

/**
 * 末端コンポーネント向けの公開 API。
 * Tracker契約（trackEventのみ）を返す。
 *
 * @knipignore
 */
export function useTracking(): Tracker {
  const value = useContext(TrackingContext);
  return { trackEvent: value?.tracking.trackEvent ?? noop };
}
